import { formikHelpers, Table } from '@round/creatorbase-ui';
import { useClientContentTags } from '@round/creatorbase-helpers/api-hooks';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import cn from 'classnames';
import { useCreatorbaseCurrencies } from 'contexts/CreatorbaseCurrencyContext';
import { mapCurrencyToOption } from 'contexts/OptionsContext/useCurrencies';
import { useInfluencerLocations } from 'Hooks/useInfluencerLocations';
import { makeLocationOption } from 'Hooks/selectOptions/useInfluencerLocationSelect';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { mapContentTagsToOptions } from 'helpers';
import { PlatformIcon, platformLabels } from 'Modules/Plans/helpers';
import { NewCreatorsFormState, toProfileHref } from '../../../../helpers';
import FieldWithError from './components/FieldWithError/FieldWithError';
import styles from './NewCreatorsTable.module.css';

const { InputField, ComboboxField, MoneyInputField } = formikHelpers;

// Rows come from classify and are never added or removed here, so they are mapped directly
// rather than through a FieldArray.
const NewCreatorsTable = () => {
    const { values } = useFormikContext<NewCreatorsFormState>();
    const { currencies } = useCreatorbaseCurrencies();
    const currencyOptions = currencies.map(mapCurrencyToOption);
    const { locations } = useInfluencerLocations();
    const countryOptions = useMemo(() => locations.map(makeLocationOption), [locations]);
    const { selectedClientId } = useClientSelect();
    const { tags: contentTags } = useClientContentTags(selectedClientId);
    const contentTagOptions = useMemo(() => mapContentTagsToOptions(contentTags), [contentTags]);

    return (
        <Table.Container className={styles.container}>
            <Table>
                <Table.Header className={styles.header}>
                    <Table.Row>
                        <Table.Head>Handle</Table.Head>
                        <Table.Head>Platform</Table.Head>
                        <Table.Head>Location</Table.Head>
                        <Table.Head>Contact details</Table.Head>
                        <Table.Head>PayPal email</Table.Head>
                        <Table.Head>Cost</Table.Head>
                        <Table.Head>Content tags</Table.Head>
                        <Table.Head>Notes</Table.Head>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {values.rows.map((row, index) => (
                        <Table.Row key={row.line}>
                            <Table.Cell>
                                <a
                                    href={toProfileHref(row.line)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.handle}
                                >
                                    @{row.handle}
                                </a>
                            </Table.Cell>

                            <Table.Cell>
                                <span className={styles.platform}>
                                    <PlatformIcon platform={row.platform} className={styles.platformIcon} />
                                    {platformLabels[row.platform]}
                                </span>
                            </Table.Cell>

                            <Table.Cell className={styles.field}>
                                <ComboboxField
                                    name={`rows.${index}.location`}
                                    items={countryOptions}
                                    placeholder="Select country"
                                />
                            </Table.Cell>

                            <Table.Cell className={styles.field}>
                                <FieldWithError name={`rows.${index}.contact_details`}>
                                    <InputField
                                        name={`rows.${index}.contact_details`}
                                        placeholder="Enter contact details"
                                    />
                                </FieldWithError>
                            </Table.Cell>

                            <Table.Cell className={styles.field}>
                                <FieldWithError name={`rows.${index}.paypal_email`}>
                                    <InputField name={`rows.${index}.paypal_email`} placeholder="Enter PayPal email" />
                                </FieldWithError>
                            </Table.Cell>

                            <Table.Cell className={styles.field}>
                                <FieldWithError name={`rows.${index}.cost`}>
                                    <MoneyInputField
                                        name={`rows.${index}.cost`}
                                        isClearable
                                        placeholder="e.g. 500"
                                        currencies={currencyOptions}
                                    />
                                </FieldWithError>
                            </Table.Cell>

                            <Table.Cell className={cn(styles.field, styles.tags)}>
                                <FieldWithError name={`rows.${index}.content_tags`}>
                                    <ComboboxField
                                        multiple
                                        isClearable
                                        maxVisibleChips={1}
                                        name={`rows.${index}.content_tags`}
                                        items={contentTagOptions}
                                        placeholder="Select tags"
                                    />
                                </FieldWithError>
                            </Table.Cell>

                            <Table.Cell className={styles.field}>
                                <InputField name={`rows.${index}.notes`} placeholder="Enter notes" />
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </Table.Container>
    );
};

export default NewCreatorsTable;
